import { ShoppingBag } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef } from "react";

import { Badge } from "@/components/ui/badge";
import { SectionHeading } from "@/components/ui/section-heading";
import { formatPrice } from "@/lib/utils";
import type { Product } from "@/types";

export function DealShelf({ products, title = "Lightning deals" }: { products: Product[]; title?: string }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isPointerDownRef = useRef(false);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftStartRef = useRef(0);
  const lastXRef = useRef(0);
  const lastTimeRef = useRef(0);
  const velocityRef = useRef(0);

  useEffect(() => {
    const handleGlobalPointerMove = (e: PointerEvent) => {
      if (!isPointerDownRef.current) return;
      const el = scrollRef.current;
      if (!el) return;

      const currentX = e.clientX;
      const deltaX = currentX - startXRef.current;

      if (!isDraggingRef.current && Math.abs(deltaX) > 4) {
        isDraggingRef.current = true;
      }

      if (isDraggingRef.current) {
        const now = performance.now();
        const dt = now - lastTimeRef.current;
        if (dt > 10) {
          velocityRef.current = (currentX - lastXRef.current) / dt;
          lastXRef.current = currentX;
          lastTimeRef.current = now;
        }
        el.scrollLeft = scrollLeftStartRef.current - deltaX;
      }
    };

    const handleGlobalPointerUp = () => {
      if (!isPointerDownRef.current) return;
      isPointerDownRef.current = false;

      const el = scrollRef.current;
      if (isDraggingRef.current && el) {
        const v = velocityRef.current;
        if (Math.abs(v) > 0.25) {
          el.scrollBy({ left: -v * 200, behavior: "smooth" });
        }
        setTimeout(() => {
          isDraggingRef.current = false;
        }, 120);
      }
    };

    window.addEventListener("pointermove", handleGlobalPointerMove, { passive: true });
    window.addEventListener("pointerup", handleGlobalPointerUp);
    window.addEventListener("pointercancel", handleGlobalPointerUp);

    return () => {
      window.removeEventListener("pointermove", handleGlobalPointerMove);
      window.removeEventListener("pointerup", handleGlobalPointerUp);
      window.removeEventListener("pointercancel", handleGlobalPointerUp);
    };
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    const el = scrollRef.current;
    if (!el) return;

    isPointerDownRef.current = true;
    isDraggingRef.current = false;
    startXRef.current = e.clientX;
    lastXRef.current = e.clientX;
    lastTimeRef.current = performance.now();
    velocityRef.current = 0;
    scrollLeftStartRef.current = el.scrollLeft;
  };

  if (products.length === 0) return null;

  return (
    <section className="mx-auto max-w-[1500px] px-3 sm:px-4">
      <div className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-2xs dark:border-white/10 dark:bg-slate-900 sm:p-6">
        <SectionHeading title={title} subtitle="Deep, time-limited discounts refreshed regularly." href="/search?sort=discount" />
        <div
          ref={scrollRef}
          onPointerDown={handlePointerDown}
          className="flex gap-3 overflow-x-auto pb-1 no-scrollbar select-none cursor-grab active:cursor-grabbing overscroll-x-contain touch-auto"
        >
          {products
            .slice()
            .sort((a, b) => b.discount - a.discount)
            .slice(0, 8)
            .map((product) => (
              <Link
                href={`/products/${product.slug}`}
                key={product.id}
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                onClick={(e) => {
                  if (isDraggingRef.current) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                className="group w-[170px] shrink-0 select-none overflow-hidden rounded-xl border border-slate-200/90 bg-white transition hover:border-[#00B7B0] hover:shadow-md dark:border-white/10 dark:bg-slate-900 sm:w-[210px]"
              >
                <div className="relative aspect-square w-full bg-[#F5F7FA] dark:bg-slate-800/90 flex items-center justify-center pointer-events-none select-none">
                  {product.images?.[0] ? (
                    <Image
                      src={product.images[0]}
                      alt={product.title}
                      fill
                      draggable={false}
                      sizes="220px"
                      className="object-contain p-2 sm:p-2.5 transition group-hover:scale-105 pointer-events-none select-none"
                    />
                  ) : (
                    <ShoppingBag className="h-10 w-10 text-slate-300 dark:text-slate-600 stroke-[1.25]" />
                  )}
                </div>
                <div className="p-2 sm:p-3 select-none">
                  <Badge tone="deal">-{product.discount}%</Badge>
                  <h3 className="mt-1.5 line-clamp-2 min-h-[2.1rem] sm:min-h-[2.4rem] text-xs sm:text-[13px] font-semibold leading-snug text-[#073B70] dark:text-white group-hover:text-[#087DB5] transition-colors">{product.title}</h3>
                  <div className="mt-1 flex items-baseline gap-1.5">
                    <span className="text-sm sm:text-base font-bold text-[#073B70] dark:text-white">{formatPrice(product.price)}</span>
                    <span className="text-[10px] sm:text-xs text-slate-400 line-through">{formatPrice(product.mrp)}</span>
                  </div>
                </div>
              </Link>
            ))}
        </div>
      </div>
    </section>
  );
}
